import React, { useCallback, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import Collection from 'Views/Components/Collection/Collection';
import { ICollectionHeaderProps } from 'Views/Components/Collection/CollectionHeaders';
import useScreenSize from 'Hooks/useScreenSize';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { contextMenu } from 'react-contexify';
import alertToast from 'Util/ToastifyUtils';
import { alertModal, confirmModal } from 'Views/Components/Modal/ModalUtils';
import { StudyPlanEntity } from 'Models/Entities';
import { useMounted } from 'Hooks/useMounted';
import { useHistory } from 'react-router';
import StudyPlanNameModal from 'Views/Pages/Frontend/StudyPlanWizard/CreatePlanModals/StudyPlanNameModal';
import { observer, useLocalStore } from 'mobx-react';
import CreateStudyPlanModal from 'Views/Pages/Frontend/StudyPlanWizard/CreatePlanModals/CreateStudyPlanModal';
import {
	getStudyPlansByUserId,
} from 'Queries/GraphQlQueries/StudyPlan/StudyPlanQueries';
import { Conditional } from 'Views/Components/Conditional/Conditional';
import useMountedQuery from 'Hooks/useMountedQuery';
import {
	availableDesktopHeaders,
	getFilterPlansGraphQlConditions,
	mobileCollectionHeaders,
} from 'Views/Components/StudyPlanCollection/StudyPlanCollectionHelpers';
import StudyPlanCollectionSearchFilter, {
	IStudyPlanFilterState,
} from 'Views/Components/StudyPlanCollection/StudyPlanCollectionSearchFilter';
import useDebounce from 'Hooks/useDebounce';
import { activateStudyPlan } from 'Queries/REST/StudyPlan/StudyPlanRequests';
import Toggle from 'Views/Components/Toggle/Toggle';
import StudyPlanDuplicateModal from 'Views/Pages/Frontend/StudyPlanWizard/CreatePlanModals/StudyPlanDuplicateModal';
import { duplicateStudyPlan } from 'Queries/REST/StudyPlan/StudyPlanRequests';
import { UserDiscriminators } from 'Models/Enums';
import { IEntityContextMenuActions } from 'Views/Components/EntityContextMenu/EntityContextMenu';
import { toLower } from 'lodash';
import Spinner from 'Views/Components/Spinner/Spinner';

export interface StudyPlanCollectionProps {
	title?: string;
	userId: string;
	showActiveToggle: boolean;
	studentView?: boolean;
	onAfterRename?: (id: string) => void;
	onAfterDelete?: (id: string) => void;
	innerRef?: React.LegacyRef<HTMLDivElement>,
	userDiscriminator?: UserDiscriminators;
	canDelete?: boolean;
}

const EmptyCollectionLabel: React.FC<{ onCreate: () => void }> = props => {
	const { onCreate } = props;

	return (
		<div className="no-plan">
			<span className="icon icon-list-alt icon-only" />
			<h6>No {toLower(window.lexicon.studyPlans)} exist</h6>
			<p className="txt-sm-body">
				Click the button below to navigate to your {toLower(window.lexicon.program)} and {toLower(window.lexicon.course)} selections and create an optimised {toLower(window.lexicon.studyPlan)}.
			</p>
			<br />
			<p className="txt-sm-body">
				Note: This is a planning tool. Creating a {toLower(window.lexicon.studyPlan)} does not check your
				eligibility to take a {toLower(window.lexicon.program)} or {toLower(window.lexicon.courses)}, or actually {toLower(window.lexicon.enrol)} you in {toLower(window.lexicon.courses)} or {toLower(window.lexicon.classDisplayName)}.
			</p>
			<Button
				display={Display.Solid}
				colors={Colors.Primary}
				onClick={onCreate}
			>
				Create {toLower(window.lexicon.studyPlan)}
			</Button>
		</div>
	);
};

const StudyPlanCollection = observer((props: StudyPlanCollectionProps) => {
	const {
		userId,
		userDiscriminator,
		showActiveToggle,
		studentView = true,
		onAfterDelete,
		onAfterRename,
		innerRef,
		title = window.lexicon.studyPlans,
		canDelete,
	} = props;

	const [page, setPage] = useState(0);
	const PAGE_SIZE = 10;

	const mounted = useMounted();
	const isMobile = useScreenSize();
	const history = useHistory();
	const [searchValue, setSearchValue] = useState<string>('');
	const filterModel = useLocalStore<IStudyPlanFilterState>(() => ({}));

	const [isSelectProgramModalOpen, setIsSelectProgramModalOpen] = useState(false);
	const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
	const [editingPlan, setEditingPlan] = useState<StudyPlanEntity>(
		new StudyPlanEntity(),
	);
	const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
	const [duplicatePlan, setDuplicatePlan] = useState<StudyPlanEntity>(
		new StudyPlanEntity(),
	);

	const [studyPlans, setStudyPlans] = useState<StudyPlanEntity[]>();
	const [totalCount, setTotalCount] = useState(0);

	const fetchStudyPlans = useCallback(
		async (abortSignal: AbortSignal) => {
			const searchQuery = `%${searchValue}%`;

			return getStudyPlansByUserId(
				userId,
				[
					[
						{
							path: 'Name',
							comparison: 'Like',
							value: [searchQuery],
							case: 'INVARIANT_CULTURE_IGNORE_CASE',
						},
					],
					...getFilterPlansGraphQlConditions(filterModel),
				],
				[],
				[
					{
						path: 'modified',
						descending: true,
					},
				],
				page * PAGE_SIZE,
				PAGE_SIZE,
				abortSignal,
			);
		},
		// Listing all `filterModel` properties as dependencies to ensure that
		// the query is re-fetched when any of the filter properties change
		//
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			filterModel.programCode,
			filterModel.programTitle,
			filterModel.commencingYear,
			filterModel.versionStatus,
			filterModel.lastModifiedAfter,
			filterModel.lastModifiedBefore,
			filterModel.createdBy,
			filterModel.modifiedBy,
			mounted,
			page,
			searchValue,
			userId,
		],
	);

	const { refresh: refreshStudyPlans } = useMountedQuery(
		fetchStudyPlans,
		[fetchStudyPlans],
		(res, isMounted) => {
			if (isMounted) {
				setStudyPlans(res.data.studyPlanEntitys.map(plan => new StudyPlanEntity(plan)));
				setTotalCount(res.data.countStudyPlanEntitys.number);
			}
		},
	);

	const setSearchDebounced = useDebounce(
		(abortSignals, newSearchValue) => setSearchValue(newSearchValue),
		[],
	);

	const editStudyPlan = (id: string) => {
		history.push(`/plan/edit/2?id=${id}`);
	};

	const editStudyPlanName = useCallback((studyPlanId: string) => {
		const studyPlan = studyPlans?.find(plan => plan.id === studyPlanId);

		if (!studyPlan) {
			alertToast(`${window.lexicon.studyPlan} not found!`, 'error');
			return;
		}

		setEditingPlan(studyPlan);
		setIsRenameModalOpen(true);
	}, [studyPlans]);

	const closeModal = useCallback(() => {
		setEditingPlan(new StudyPlanEntity());
		setIsRenameModalOpen(false);
		setIsSelectProgramModalOpen(false);
		setIsDuplicateModalOpen(false);
		setDuplicatePlan(new StudyPlanEntity());
	}, []);

	const createPlan = async (newStudyPlan: StudyPlanEntity) => {
		// if specific, assigned to a user
		// if not, let the backend determine this automatically
		if (userDiscriminator) {
			newStudyPlan.assignToUser(userId, userDiscriminator);
		}
		await newStudyPlan.save();

		// early return if component unmounted
		if (!mounted.current) {
			return;
		}

		closeModal();
		editStudyPlan(newStudyPlan.id);
	};

	const renamePlan = async (updatedPlan: StudyPlanEntity) => {
		await updatedPlan.save();

		// early return if component unmounted
		if (!mounted.current) {
			return;
		}

		closeModal();

		refreshStudyPlans();
		onAfterRename?.(updatedPlan.id);
	};

	const onDeleteStudyPlan = async (studyPlanId: string, isPlanActive: boolean) => {
		// if the plan is active, prevent deletion
		if (isPlanActive) {
			return alertModal(
				'Cannot delete active plan',
				(
					<p>
						Before this {window.lexicon.studyPlan} can be deleted, another {window.lexicon.studyPlan} must be made active.
					</p>
				),
				{ cancelText: 'Dismiss' },
			);
		}

		// if the plan is inactive, proceed with confirmation and deletion
		return confirmModal(
			'Delete study plan?',
			(
				<p>
					Deleting will remove the {window.lexicon.studyPlan} from your list. This action cannot be undone.
				</p>
			),
			{
				resolveOnCancel: true,
			},
		).then(actionConfirmed => {
			// if rejected or componet unmounted
			if (!actionConfirmed || !mounted.current) {
				return;
			}

			const studyPlanToDelete = studyPlans?.find(plan => plan.id === studyPlanId);

			if (!studyPlanToDelete) {
				alertToast('Cannot find study plan to delete', 'error');
				return;
			}

			studyPlanToDelete.delete()
				.then(() => {
					refreshStudyPlans();
					alertToast('Study plan deleted', 'success');
				})
				.catch(() => alertToast('Cannot delete study plan', 'error'))
				.finally(() => onAfterDelete?.(studyPlanToDelete.id));
		});
	};

	const onActivateStudyPlan = async (studyPlan: StudyPlanEntity) => {
		if (studyPlan.active) {
			await alertModal(
				'Unable to deactivate',
				(
					<p>
						You must have at least one active {window.lexicon.studyPlan}.
						To deactivate this plan, please activate another {window.lexicon.studyPlan}.
					</p>
				),
				{
					cancelText: 'Okay',
				},
			);
			return;
		}

		await confirmModal(
			'Activate study plan',
			(
				<p>
					Are you sure you want to activate <strong>`{studyPlan.name}`</strong> {toLower(window.lexicon.studyPlan)}`?
				</p>
			),
			{
				confirmText: 'Activate',
				cancelText: 'Cancel',
				resolveOnCancel: true,
			},
		).then(async actionConfirmed => {
			if (!actionConfirmed) {
				return;
			}

			// early return if component unmounted
			if (!mounted.current) {
				return;
			}

			await activateStudyPlan(studyPlan.id)
				.then(rs => {
					refreshStudyPlans();
				})
				.catch(err => alertToast('Activate failed', 'error'));
		});
	};

	const onConfirmDuplicateModal = async (studyPlanId: string, name: string) => {
		await duplicateStudyPlan(studyPlanId, name).then(res => {
			refreshStudyPlans();
			closeModal();
		}).catch(err => alertToast(err.message, 'warning'));
	};

	const onOpenDuplicateModal = (studyPlan: StudyPlanEntity) => {
		const isExist = studyPlans?.find(plan => plan.id === studyPlan?.id);

		if (!isExist) {
			alertToast('Study plan not found!', 'error');
			return;
		}
		setDuplicatePlan(studyPlan);
		setIsDuplicateModalOpen(true);
	};

	const onCreate = () => {
		setIsSelectProgramModalOpen(true);
		setIsRenameModalOpen(false);
	};

	const collectionHeaders = () => {
		if (isMobile) {
			return mobileCollectionHeaders;
		}

		let newCollectionHeaders: Array<ICollectionHeaderProps<StudyPlanEntity>> = availableDesktopHeaders;

		if (showActiveToggle) {
			const activeStatusIndex = newCollectionHeaders
				.findIndex(header => header.name === 'active');

			if (activeStatusIndex) {
				newCollectionHeaders[activeStatusIndex].transformItem = (item: StudyPlanEntity) => (
					<Toggle
						ariaLabel="active study plan toggle"
						onLabel="Yes"
						offLabel="No"
						checked={item.active}
						onChange={() => onActivateStudyPlan(item)}
					/>
				);
			}
		} else {
			newCollectionHeaders = newCollectionHeaders
				.filter(header => header.name !== 'active');
		}

		if (studentView) {
			newCollectionHeaders = newCollectionHeaders
				.filter(header => header.name !== 'programVersionStatus');
		}

		return newCollectionHeaders;
	};

	return (
		<div className="study-plans-collection-wrapper" ref={innerRef} key={`${userId}`}>
			<div className="study-plans-collection-title">
				<h4 className="title">
					{title}
					<span className="study-plan-count">
						{studyPlans?.length}
					</span>
				</h4>
				<div className="action-buttons">
					<Button
						className={isMobile ? 'btn--icon-only' : ''}
						testId="create-study-plan-btn"
						display={Display.Solid}
						colors={Colors.Primary}
						icon={{
							iconPos: 'icon-right',
							icon: 'add',
						}}
						onClick={onCreate}
						buttonProps={{ 'aria-label': 'Create study plan' }}
						disabled={studyPlans === undefined}
					>
						{!isMobile ? `Create ${toLower(window.lexicon.studyPlan)}` : ''}
					</Button>
				</div>
			</div>
			<Conditional
				condition={!studentView}
				component={StudyPlanCollectionSearchFilter}
				componentProps={{
					userId,
					filterState: filterModel,
					onSearch: setSearchDebounced,
				}}
			/>
			<Tooltip id="role-tooltip" />
			<Collection<StudyPlanEntity>
				idColumn="id"
				className="study-plans-collection"
				ariaLabel="Study plans list"
				collection={
					// sort by time, descending (newest to oldest)
					studyPlans?.sort((a, b) => b.modified.getTime() - a.modified.getTime()) || []
				}
				headers={collectionHeaders()}
				noDataMessage={studyPlans !== undefined ? <EmptyCollectionLabel onCreate={onCreate} /> : <Spinner />}
				actionsMore={[
					{
						icon: 'edit',
						iconPos: 'icon-left',
						label: `Edit ${toLower(window.lexicon.studyPlan)}`,
						onEntityClick: (event, entity) => {
							event.event.stopPropagation();
							contextMenu.hideAll();
							editStudyPlan(entity.id!);
						},
					},
					{
						icon: 'refresh',
						iconPos: 'icon-left',
						label: `Rename ${toLower(window.lexicon.studyPlan)}`,
						onEntityClick: (event, entity) => {
							event.event.stopPropagation();
							editStudyPlanName(entity.id!);
							contextMenu.hideAll();
						},
					},
					{
						icon: 'file-copy',
						iconPos: 'icon-left',
						label: `Duplicate ${toLower(window.lexicon.studyPlan)}`,
						onEntityClick: (event, entity) => {
							event.event.stopPropagation();
							onOpenDuplicateModal(entity);
							contextMenu.hideAll();
						},
					},
					...(
						canDelete
							? [{
								icon: 'delete',
								iconPos: 'icon-left',
								label: 'Delete',
								onEntityClick: (event, studyPlan) => {
									event.event.stopPropagation();
									onDeleteStudyPlan(studyPlan.id!, studyPlan.active);
									contextMenu.hideAll();
								},
							}] as IEntityContextMenuActions<StudyPlanEntity> : []
					),
				]}
				onRowClick={rowData => editStudyPlan(rowData.id)}
				onPageChange={setPage}
				pageNo={page}
				perPage={PAGE_SIZE}
				totalRecords={totalCount}
			/>
			<StudyPlanNameModal
				initialPlan={editingPlan}
				isOpen={isRenameModalOpen}
				closeModal={closeModal}
				onConfirm={renamePlan}
				existingStudyPlanNames={
					studyPlans?.filter(plan => plan.id !== editingPlan.id)
						.map(plan => plan.name)
				}
			/>
			<CreateStudyPlanModal
				initialPlan={editingPlan}
				isOpen={isSelectProgramModalOpen}
				closeModal={closeModal}
				confirmModal={createPlan}
			/>
			<StudyPlanDuplicateModal
				isOpen={isDuplicateModalOpen}
				closeModal={closeModal}
				onConfirm={onConfirmDuplicateModal}
				initialPlan={duplicatePlan}
			/>
		</div>
	);
});

export default StudyPlanCollection;
